import {
  COMMISSION_TABLE_COLUMNS,
  GENERAL_TABLE_COLUMNS,
} from "./commissionsConstants";

const STORAGE_PREFIX = "cms.commissions.columns.v1";

const LOCKED_COLUMN_KEYS = new Set(["select", "rowNumber", "actions"]);

export function getCommissionsTableColumnDefinitions(report = "general") {
  const base =
    report === "commission" ? COMMISSION_TABLE_COLUMNS : GENERAL_TABLE_COLUMNS;
  const selectIdx = base.findIndex((column) => column.key === "select");
  if (selectIdx === -1) {
    return [
      { title: "#", key: "rowNumber", sortable: false, canToggle: false },
      ...base,
    ];
  }

  return [
    ...base.slice(0, selectIdx + 1),
    { title: "#", key: "rowNumber", sortable: false, canToggle: false },
    ...base.slice(selectIdx + 1),
  ];
}

function getStorageKey(report) {
  // v2 commission defaults: Status on, Batch off (see COMMISSION_TABLE_COLUMNS).
  const version = report === "commission" ? "v2" : "v1";
  return `${STORAGE_PREFIX}.${version}.${report}`;
}

export function getDefaultVisibleColumnKeys(report = "general") {
  return getCommissionsTableColumnDefinitions(report)
    .filter((column) => column.canToggle === false || column.defaultVisible !== false)
    .map((column) => column.key);
}

export function loadVisibleColumnKeys(report = "general") {
  const allColumns = getCommissionsTableColumnDefinitions(report);
  const lockedKeys = allColumns
    .filter((column) => column.canToggle === false || LOCKED_COLUMN_KEYS.has(column.key))
    .map((column) => column.key);
  const toggleableKeys = new Set(
    allColumns
      .filter((column) => column.canToggle !== false && !LOCKED_COLUMN_KEYS.has(column.key))
      .map((column) => column.key),
  );

  try {
    const raw = localStorage.getItem(getStorageKey(report));
    if (!raw) {
      return getDefaultVisibleColumnKeys(report);
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return getDefaultVisibleColumnKeys(report);
    }

    const selected = parsed.filter((key) => toggleableKeys.has(key));
    return [...new Set([...lockedKeys, ...selected])];
  } catch {
    return getDefaultVisibleColumnKeys(report);
  }
}

export function saveVisibleColumnKeys(report = "general", visibleKeys = []) {
  const allColumns = getCommissionsTableColumnDefinitions(report);
  const toggleableKeys = allColumns
    .filter((column) => column.canToggle !== false && !LOCKED_COLUMN_KEYS.has(column.key))
    .map((column) => column.key);

  const stored = toggleableKeys.filter((key) => visibleKeys.includes(key));

  try {
    localStorage.setItem(getStorageKey(report), JSON.stringify(stored));
  } catch {
    // Ignore quota / private mode errors.
  }
}

export function resolveVisibleColumns(report = "general", visibleKeys = []) {
  const allColumns = getCommissionsTableColumnDefinitions(report);
  const visibleSet = new Set(visibleKeys);
  return allColumns.filter((column) => visibleSet.has(column.key));
}

export function getToggleableColumns(report = "general") {
  return getCommissionsTableColumnDefinitions(report).filter(
    (column) => column.canToggle !== false && !LOCKED_COLUMN_KEYS.has(column.key),
  );
}
